import { useEffect, useState } from 'react'
import { BookingApp } from '@/features/booking'
import type { LinkParams } from '@/features/clinic'
import { BookingPageNotFound, EntryPanel } from '@/features/entry'
import { useEmbedHost, type HostBridge } from '@/features/frame-bridge'

/**
 * `/c/{slug}/embed` (Frontend §4.1; D-36): the iframe embed.js opens. Entry screens in the panel layout, booking
 * steps in the overlay layout, no browser history entries. `existing=1` and `utm_source` are ignored: the embed's
 * source is `data-source` (`?src=`) and it always asks new or existing. `?p=` is `data-preset` (D-79).
 */
export function EmbedPage({ slug, link, bridge }: { slug: string; link: LinkParams; bridge: HostBridge }) {
  const [embedLink] = useState<LinkParams>(() => ({
    src: link.src,
    utmSource: null,
    existingOnly: false,
    // `?p=` from embed.js `data-preset` (task 22, D-79).
    preset: link.preset ?? null,
  }))
  const embed = useEmbedHost(bridge, slug, embedLink.src, embedLink.preset ?? null)
  useEffect(() => {
    const html = document.documentElement
    html.classList.add('pa-embed')
    return () => {
      html.classList.remove('pa-embed')
    }
  }, [])
  return (
    <div className="pt pt-frame">
      <BookingApp slug={slug} link={embedLink} embed={embed} />
    </div>
  )
}

/** An embed path with a malformed slug (T-P00-07): the panel says the booking page doesn't exist; X closes it. */
export function EmbedNotFoundPage({ bridge }: { bridge: HostBridge }) {
  useEffect(() => {
    const html = document.documentElement
    html.classList.add('pa-embed')
    // No visit was started: the host's close needs no beacon.
    const off = bridge.onClose(() => {
      bridge.post({ type: 'closed' })
    })
    return () => {
      off()
      html.classList.remove('pa-embed')
    }
  }, [bridge])
  return (
    <div className="pt pt-frame">
      <EntryPanel
        clinicName={undefined}
        variant="embed"
        onClose={() => {
          bridge.post({ type: 'close' })
        }}
        rootRef={(el) => {
          if (el) {
            void bridge.settled.then(() => {
              bridge.post({ type: 'resize', height: Math.ceil(el.getBoundingClientRect().height) })
            })
          }
        }}
      >
        <BookingPageNotFound variant="panel" />
      </EntryPanel>
    </div>
  )
}
